<script>
import { useAlert } from 'dashboard/composables';
import { format, parseISO } from 'date-fns';
import SectionCard from '../SectionCard.vue';
import AssignmentModal from '../modals/AssignmentModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import {
  listAssignments,
  deleteAssignment,
} from '../../../../api/realEstate/assignments';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import { listAllPages } from '../../../../api/realEstate/pagination';

// Layer 1 — one-off assignments, filtered by this project — fetched
// directly here (not Vuex), same single-screen approach as the other
// Phase 2 sections (docs/real-estate/be/10-api-reference.md §A10).
export default {
  name: 'AssignmentsSection',
  components: { SectionCard, AssignmentModal, DeleteModal },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      assignments: [],
      isLoading: false,
      isModalOpen: false,
      editingAssignment: null,
      assignmentToDelete: null,
      isDeleting: false,
    };
  },
  watch: {
    'project.property_id': {
      immediate: true,
      handler() {
        this.loadAssignments();
      },
    },
  },
  methods: {
    async loadAssignments() {
      this.isLoading = true;
      try {
        this.assignments = await listAllPages(listAssignments, {
          property_id: this.project.property_id,
        });
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.ASSIGNMENTS.ERROR_LOAD')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    agentName(agentId) {
      const agent = this.project.agents.find(a => a.agent_id === agentId);
      return agent ? agent.name : agentId;
    },
    formatDate(d) {
      return format(parseISO(d), 'd MMM yyyy');
    },
    formatDateRange(startDate, endDate) {
      return `${this.formatDate(startDate)} – ${this.formatDate(endDate)}`;
    },
    formatTimeSlots(timeSlots) {
      if (!timeSlots.length) return '—';
      return timeSlots
        .map(slot => `${slot.start_time}–${slot.end_time}`)
        .join(', ');
    },
    openCreateModal() {
      this.editingAssignment = null;
      this.isModalOpen = true;
    },
    openEditModal(assignment) {
      this.editingAssignment = assignment;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.editingAssignment = null;
    },
    handleSaved() {
      this.closeModal();
      this.loadAssignments();
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await deleteAssignment(this.assignmentToDelete.assignment_id);
        useAlert(this.$t('REAL_ESTATE.ASSIGNMENTS.SUCCESS_DELETED'));
        this.assignmentToDelete = null;
        await this.loadAssignments();
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.ASSIGNMENTS.ERROR_DELETE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<template>
  <SectionCard
    section-id="assignments"
    :title="$t('REAL_ESTATE.ASSIGNMENTS.TITLE')"
  >
    <template #action>
      <woot-button
        variant="clear"
        size="small"
        icon="add"
        @click="openCreateModal"
      >
        {{ $t('REAL_ESTATE.ASSIGNMENTS.ADD_BUTTON') }}
      </woot-button>
    </template>

    <AssignmentModal
      v-if="isModalOpen"
      :project="project"
      :assignment="editingAssignment"
      @close="closeModal"
      @saved="handleSaved"
    />
    <DeleteModal
      v-if="assignmentToDelete"
      :delete-name="agentName(assignmentToDelete.agent_id)"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
      @close="assignmentToDelete = null"
    />

    <p v-if="isLoading" class="text-sm text-slate-400">
      {{ $t('REAL_ESTATE.ASSIGNMENTS.LOADING') }}
    </p>
    <p v-else-if="!assignments.length" class="text-sm text-slate-400">
      {{ $t('REAL_ESTATE.ASSIGNMENTS.EMPTY') }}
    </p>
    <table v-else class="w-full text-sm border-collapse">
      <thead>
        <tr
          class="text-left text-slate-400 border-b border-slate-200 dark:border-slate-700"
        >
          <th class="py-2 pr-4 font-medium">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.COLUMN_AGENT') }}
          </th>
          <th class="py-2 pr-4 font-medium">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.COLUMN_DATES') }}
          </th>
          <th class="py-2 pr-4 font-medium">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.COLUMN_TIMES') }}
          </th>
          <th class="py-2 pr-4 font-medium text-right">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.COLUMN_ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="assignment in assignments"
          :key="assignment.assignment_id"
          class="border-b border-slate-100 dark:border-slate-800"
        >
          <td class="py-2 pr-4 text-slate-900 dark:text-slate-100 font-medium">
            {{ agentName(assignment.agent_id) }}
          </td>
          <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
            {{ formatDateRange(assignment.start_date, assignment.end_date) }}
          </td>
          <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
            {{ formatTimeSlots(assignment.time_slots) }}
          </td>
          <td class="py-2 pr-4 text-right">
            <woot-button
              variant="clear"
              size="small"
              icon="edit"
              @click="openEditModal(assignment)"
            />
            <woot-button
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              @click="assignmentToDelete = assignment"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </SectionCard>
</template>
